import React, { useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FlagIcon from '@mui/icons-material/Flag';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { ScreeningAiEvaluation, ExportProduct } from 'api/ExportHubAPI';
import {
  Severity,
  sevPillClass,
  sevTextClass,
  sevBoxClass,
  DrawerShell,
} from './productDetailShell';

/* ─── Compliance score model (client-derived — no backend score exists) ─── */
// Export has no numeric compliance score; ExportProduct carries only a
// categorical compliance_status. Per the resolved §0 decision we mirror the
// import page: derive a 0-100 score client-side from real product fields.

export interface ScoreDeduction {
  rule: string;
  reason: string;
  points: number;
  category: string;
  passed: boolean;
}

const jurisdictionDetail = (p: ExportProduct, key: string): string => {
  const v = p.jurisdiction_details?.[key];
  return typeof v === 'string' ? v : '';
};

export function buildScoreDeductions(
  p: ExportProduct,
  htsInvalid: boolean,
): ScoreDeduction[] {
  const needsUsml = p.jurisdiction === 'ITAR' && !jurisdictionDetail(p, 'usml_category');
  const needsEccn =
    p.jurisdiction === 'EAR' && !(jurisdictionDetail(p, 'eccn') || p.eccn);
  return [
    {
      rule: 'HTS / Schedule B code present',
      reason: 'Every product must carry a valid Schedule B / HTS code.',
      points: 10,
      category: 'Classification',
      passed: !!p.hts_code,
    },
    {
      rule: 'HTS code valid',
      reason: 'The recorded HTS code is current — no replacement required.',
      points: 10,
      category: 'Classification',
      passed: !htsInvalid,
    },
    {
      rule: 'Jurisdiction detail complete',
      reason: 'ITAR requires a USML category; EAR requires an ECCN.',
      points: 10,
      category: 'Jurisdiction',
      passed: !(needsUsml || needsEccn),
    },
    {
      rule: 'Product screened',
      reason: 'Analyze the product at least once against all six checks.',
      points: 8,
      category: 'Screening',
      passed: !!p.last_screened_at,
    },
    {
      rule: 'At least one linked license',
      reason: 'A controlled product needs license coverage for its destinations.',
      points: 6,
      category: 'Licenses',
      passed: p.active_license_count > 0,
    },
    {
      rule: 'Manufacturer captured',
      reason: 'Manufacturer is required for provenance.',
      points: 4,
      category: 'Classification',
      passed: !!p.manufacturer,
    },
  ];
}

export function computeScore(dedu: ScoreDeduction[]): number {
  const lost = dedu.filter((d) => !d.passed).reduce((s, d) => s + d.points, 0);
  return Math.max(0, 100 - lost);
}

export function scoreBand(score: number): { band: Severity; label: string } {
  if (score >= 90) return { band: 'success', label: 'Good' };
  if (score >= 70) return { band: 'warning', label: 'Fair' };
  return { band: 'danger', label: 'Low' };
}

/* ─── System flags ─── */

export interface SystemFlagItem {
  label: string;
  detail?: string;
  severity: Severity;
  anchor?: string;
}

export function buildSystemFlags(
  p: ExportProduct,
  htsInvalid: boolean,
  coverageGap: string[],
): SystemFlagItem[] {
  const out: SystemFlagItem[] = [];
  if (p.jurisdiction === 'ITAR' && !jurisdictionDetail(p, 'usml_category')) {
    out.push({
      label: 'Missing Jurisdiction Detail',
      detail: 'Jurisdiction detail required for ITAR (USML category).',
      severity: 'warning',
      anchor: 'jurisdiction',
    });
  }
  if (p.jurisdiction === 'EAR' && !(jurisdictionDetail(p, 'eccn') || p.eccn)) {
    out.push({
      label: 'Missing ECCN',
      detail: 'EAR jurisdiction requires an ECCN.',
      severity: 'warning',
      anchor: 'jurisdiction',
    });
  }
  if (!p.hts_code || htsInvalid) {
    out.push({
      label: htsInvalid ? 'Invalid / Expired HTS' : 'Missing ECCN/USML',
      detail: htsInvalid
        ? 'Recommended replacement codes are available.'
        : 'Assign a Schedule B code.',
      severity: 'danger',
      anchor: 'classification',
    });
  }
  if (!p.last_screened_at) {
    out.push({
      label: 'Never Screened',
      detail: 'Run Analyze Product to establish a baseline.',
      severity: 'warning',
      anchor: 'screening',
    });
  }
  if (p.active_license_count === 0) {
    out.push({
      label: 'No Licenses Linked',
      detail: 'Link an export license or add a new one.',
      severity: 'warning',
      anchor: 'licenses',
    });
  }
  if (coverageGap.length) {
    out.push({
      label: 'Destinations Without Coverage',
      detail: `${coverageGap.join(', ')} not covered by linked licenses.`,
      severity: 'danger',
      anchor: 'licenses',
    });
  }
  return out;
}

const scrollToAnchor = (anchor?: string) => {
  if (!anchor) return;
  const el = document.getElementById(anchor);
  if (el)
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - 84,
      behavior: 'smooth',
    });
};

/* ─── Box 1 — Compliance Score ─── */

const ScoreBar: React.FC<{ score: number; band: Severity }> = ({ score, band }) => (
  <div className='eh-scorebar'>
    <div
      className={`eh-scorebar-fill eh-sev-bg-${band}`}
      style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
    />
  </div>
);

export const ScoreCard: React.FC<{
  score: number;
  band: Severity;
  bandLabel: string;
  deductionCount: number;
  onOpen: () => void;
}> = ({ score, band, bandLabel, deductionCount, onOpen }) => (
  <button type='button' className='eh-kpi-card' onClick={onOpen}>
    <div className='eh-kpi-top'>
      <span className={`eh-kpi-eyebrow ${sevTextClass(band)}`}>
        <EmojiEventsIcon style={{ fontSize: 14 }} /> Compliance Score
      </span>
      <span className={sevPillClass(band)}>{bandLabel}</span>
    </div>
    <div className='eh-kpi-scoreline'>
      <span className='eh-kpi-score'>{score}</span>
      <span className='eh-kpi-score-max'>/ 100</span>
    </div>
    <ScoreBar score={score} band={band} />
    <div className='eh-kpi-foot'>
      <span>
        {deductionCount > 0
          ? `${deductionCount} deduction${deductionCount > 1 ? 's' : ''} applied`
          : 'No deductions applied'}
      </span>
      <span className='eh-kpi-cta'>
        How we calculate your score <ChevronRightIcon style={{ fontSize: 13 }} />
      </span>
    </div>
  </button>
);

/* ─── Box 2 — System Flags ─── */

const SEV_RANK: Record<Severity, number> = {
  danger: 0,
  warning: 1,
  info: 2,
  success: 3,
};

export const SystemFlagsCard: React.FC<{ flags: SystemFlagItem[] }> = ({ flags }) => {
  const [allOpen, setAllOpen] = useState(false);
  const worst: Severity = flags.some((f) => f.severity === 'danger')
    ? 'danger'
    : flags.some((f) => f.severity === 'warning')
    ? 'warning'
    : flags.length
    ? 'info'
    : 'success';
  const sorted = [...flags].sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity]);
  const recent = sorted.slice(0, 3);

  return (
    <>
      <div className='eh-kpi-card eh-kpi-card-static'>
        <div className='eh-kpi-top'>
          <span className={`eh-kpi-eyebrow ${sevTextClass(worst)}`}>
            <FlagIcon style={{ fontSize: 14 }} /> System Flags
          </span>
          <span className='eh-kpi-count'>{flags.length} total</span>
        </div>
        {flags.length === 0 ? (
          <div className={`${sevBoxClass('success')} eh-kpi-clean`}>
            <CheckCircleIcon style={{ fontSize: 16 }} /> No system flags on this
            product.
          </div>
        ) : (
          <ul className='eh-kpi-flags'>
            {recent.map((f, i) => (
              <li key={i}>
                <button
                  type='button'
                  className={`eh-kpi-flag ${sevBoxClass(f.severity)}`}
                  onClick={() => scrollToAnchor(f.anchor)}
                >
                  <WarningAmberIcon
                    className={sevTextClass(f.severity)}
                    style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}
                  />
                  <span className='eh-kpi-flag-text'>
                    <span className='eh-kpi-flag-label'>{f.label}</span>
                    {f.detail && <span className='eh-kpi-flag-detail'>{f.detail}</span>}
                  </span>
                  <ChevronRightIcon
                    style={{ fontSize: 14, flexShrink: 0, opacity: 0.6 }}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
        {flags.length > 3 && (
          <button
            type='button'
            className='eh-kpi-cta eh-kpi-viewall'
            onClick={() => setAllOpen(true)}
          >
            View all {flags.length} flags <ChevronRightIcon style={{ fontSize: 13 }} />
          </button>
        )}
      </div>
      <DrawerShell
        open={allOpen}
        eyebrow='System'
        title={`System Flags (${flags.length})`}
        onClose={() => setAllOpen(false)}
      >
        <div className='eh-drawer-stack'>
          {sorted.map((f, i) => (
            <button
              key={i}
              type='button'
              className={`eh-kpi-flag ${sevBoxClass(f.severity)}`}
              onClick={() => {
                setAllOpen(false);
                scrollToAnchor(f.anchor);
              }}
            >
              <WarningAmberIcon
                className={sevTextClass(f.severity)}
                style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
              />
              <span className='eh-kpi-flag-text'>
                <span className='eh-kpi-flag-label'>{f.label}</span>
                {f.detail && <span className='eh-kpi-flag-detail'>{f.detail}</span>}
              </span>
            </button>
          ))}
        </div>
      </DrawerShell>
    </>
  );
};

/* ─── Box 3 — Risk (from export_screening ai_evaluation) ─── */

const riskTone = (risk: string): Severity =>
  risk === 'Critical'
    ? 'danger'
    : risk === 'High'
    ? 'warning'
    : risk === 'Medium'
    ? 'info'
    : 'success';
const burdenTone = (burden: string): Severity =>
  burden === 'High' ? 'danger' : burden === 'Medium' ? 'warning' : 'success';

export const RiskCard: React.FC<{
  running: boolean;
  evaluation: ScreeningAiEvaluation | null;
  onRun: () => void;
  onOpen: () => void;
}> = ({ running, evaluation, onRun, onOpen }) => {
  if (!evaluation) {
    return (
      <div className='eh-kpi-card eh-kpi-card-static eh-kpi-risk-empty'>
        <span className='eh-kpi-eyebrow eh-sev-muted'>
          <AutoAwesomeIcon style={{ fontSize: 14 }} /> Risk
        </span>
        <div className='eh-kpi-risk-cta'>
          <span className='eh-kpi-risk-icon'>
            <AutoAwesomeIcon style={{ fontSize: 18 }} />
          </span>
          <p className='eh-kpi-risk-blurb'>
            Generate an AI risk evaluation — risk level, burden, key factors and
            recommendation.
          </p>
          <button
            type='button'
            className='btn-primary'
            onClick={onRun}
            disabled={running}
          >
            {running ? (
              <>
                <AutorenewIcon className='eh-spin' style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }} />
                Running…
              </>
            ) : (
              <>
                <PlayCircleOutlineIcon style={{ fontSize: 15, marginRight: 6, verticalAlign: 'middle' }} />
                Run Risk Evaluation
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
  const rTone = riskTone(evaluation.risk);
  const bTone = burdenTone(evaluation.burden);
  return (
    <button type='button' className='eh-kpi-card' onClick={onOpen}>
      <div className='eh-kpi-top'>
        <span className={`eh-kpi-eyebrow ${sevTextClass(rTone)}`}>
          <AutoAwesomeIcon style={{ fontSize: 14 }} /> Risk
        </span>
        {evaluation.date && (
          <span className='eh-kpi-count'>
            <AccessTimeIcon style={{ fontSize: 12, verticalAlign: 'middle', marginRight: 3 }} />
            {evaluation.date}
          </span>
        )}
      </div>
      <div className='eh-kpi-riskgrid'>
        <div>
          <div className='eh-kpi-risklabel'>Risk</div>
          <div className={`eh-kpi-riskval ${sevTextClass(rTone)}`}>{evaluation.risk}</div>
        </div>
        <div>
          <div className='eh-kpi-risklabel'>Burden</div>
          <div className={`eh-kpi-riskval ${sevTextClass(bTone)}`}>
            {evaluation.burden}
          </div>
        </div>
      </div>
      <div className='eh-kpi-foot'>
        <span className='eh-kpi-cta'>
          View full evaluation <ChevronRightIcon style={{ fontSize: 13 }} />
        </span>
      </div>
    </button>
  );
};

/* ─── Drawers ─── */

export const ScoreDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  score: number;
  band: Severity;
  bandLabel: string;
  deductions: ScoreDeduction[];
}> = ({ open, onClose, score, band, bandLabel, deductions }) => {
  const failed = deductions.filter((d) => !d.passed);
  const passed = deductions.filter((d) => d.passed);
  const totalDeducted = failed.reduce((s, d) => s + d.points, 0);
  return (
    <DrawerShell open={open} eyebrow='Compliance' title='Score Breakdown' onClose={onClose}>
      <div className='eh-score-summary'>
        <div className='eh-score-summary-top'>
          <div>
            <div className='eh-drawer-eyebrow'>This product</div>
            <div className='eh-kpi-scoreline'>
              <span className='eh-kpi-score' style={{ fontSize: 30 }}>
                {score}
              </span>
              <span className='eh-kpi-score-max'>/ 100</span>
            </div>
          </div>
          <span className={sevPillClass(band)}>{bandLabel}</span>
        </div>
        <ScoreBar score={score} band={band} />
        <div className='eh-score-summary-note'>
          {failed.length > 0 ? (
            <>
              Total deductions applied:{' '}
              <span className={sevTextClass('danger')}>{totalDeducted}%</span> across{' '}
              {failed.length} rule{failed.length > 1 ? 's' : ''}.
            </>
          ) : (
            <>All scored rules passed.</>
          )}
        </div>
      </div>

      <div className='eh-score-group'>
        <div className='eh-score-group-title'>Deductions</div>
        {failed.length === 0 ? (
          <div className={`${sevBoxClass('success')} eh-score-clean`}>No points lost.</div>
        ) : (
          <ul className='eh-drawer-stack'>
            {failed.map((d, i) => (
              <li key={i} className={`eh-score-row ${sevBoxClass('danger')}`}>
                <ErrorOutlineIcon
                  className={sevTextClass('danger')}
                  style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}
                />
                <div className='eh-score-row-body'>
                  <div className='eh-score-row-head'>
                    <span className='eh-score-row-rule'>{d.rule}</span>
                    <span className={`eh-score-row-pts ${sevTextClass('danger')}`}>
                      −{d.points}%
                    </span>
                  </div>
                  <p className='eh-score-row-reason'>{d.reason}</p>
                  <div className='eh-score-row-cat'>{d.category}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {passed.length > 0 && (
        <div className='eh-score-group'>
          <div className='eh-score-group-title'>Passed</div>
          <ul className='eh-drawer-stack'>
            {passed.map((d, i) => (
              <li key={i} className='eh-score-row eh-sev-box eh-score-row-passed'>
                <CheckCircleIcon
                  className={sevTextClass('success')}
                  style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}
                />
                <div className='eh-score-row-body'>
                  <div className='eh-score-row-rule'>{d.rule}</div>
                  <p className='eh-score-row-reason'>{d.reason}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DrawerShell>
  );
};

export const RiskDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  evaluation: ScreeningAiEvaluation | null;
}> = ({ open, onClose, evaluation }) => (
  <DrawerShell open={open} eyebrow='AI' title='Risk Evaluation' onClose={onClose}>
    {!evaluation ? (
      <p className='eh-drawer-muted'>No risk evaluation has been run yet.</p>
    ) : (
      <div className='eh-drawer-stack'>
        <div className='eh-ai-summary'>
          <div className='eh-ai-metrics'>
            <div>
              <div className='eh-kpi-risklabel'>Risk</div>
              <div className={`eh-kpi-riskval ${sevTextClass(riskTone(evaluation.risk))}`}>
                {evaluation.risk}
              </div>
            </div>
            <div>
              <div className='eh-kpi-risklabel'>Burden</div>
              <div
                className={`eh-kpi-riskval ${sevTextClass(burdenTone(evaluation.burden))}`}
              >
                {evaluation.burden}
              </div>
            </div>
          </div>
          {evaluation.summary && <p className='eh-ai-text'>{evaluation.summary}</p>}
        </div>
        {evaluation.key_factors?.length > 0 && (
          <div className='eh-soft-box'>
            <div className='eh-drawer-eyebrow'>Key factors</div>
            <ul className='eh-ai-factors'>
              {evaluation.key_factors.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </DrawerShell>
);
